import React, { useEffect, useState, useCallback, useMemo } from "react";
import Filters from "../components/Filters";
import { ErrorState } from "../components/EmptyState";
import { api, fmtNum, monthLabel } from "../lib/api";
import { CheckCircle2, XCircle, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function StatusPelaporan() {
  const [filter, setFilter] = useState({ tahun: "2026", bulan: "september", rw: "", rt: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const nav = useNavigate();

  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try { setData(await api.status(filter.tahun, filter.bulan)); }
    catch { setError(true); } finally { setLoading(false); }
  }, [filter.tahun, filter.bulan]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const list = data?.data || [];
    return filter.rw ? list.filter((r) => r.rw === filter.rw) : list;
  }, [data, filter.rw]);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0e2b26] tracking-tight">Status Pelaporan</h1>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
          Periode <strong className="text-[#0e2b26]">{monthLabel(filter.bulan)} {filter.tahun}</strong> · Total <strong>{data?.total_rt || 75}</strong> RT
        </p>
      </div>

      <Filters value={filter} onChange={setFilter} onRefresh={load} showRw />

      {!loading && !error && data && (
        <div className="grid grid-cols-3 gap-3">
          <div className="stat-card !p-3.5">
            <div className="stat-label">Total RT</div>
            <div className="stat-value !text-2xl">{data.total_rt}</div>
          </div>
          <div className="stat-card !p-3.5" data-testid="stat-sudah">
            <div className="stat-label">Sudah Mengisi</div>
            <div className="stat-value !text-2xl text-emerald-700">{data.sudah_mengisi}</div>
          </div>
          <div className="stat-card !p-3.5" data-testid="stat-belum">
            <div className="stat-label">Belum Mengisi</div>
            <div className="stat-value !text-2xl text-red-600">{data.belum_mengisi}</div>
          </div>
        </div>
      )}

      {error ? (
        <ErrorState onRetry={load} />
      ) : (
        <div className="bg-white rounded-xl border border-[hsl(var(--border))] overflow-hidden">
          {/* Desktop table */}
          <div className="hidden md:block table-scroll">
            <table className="w-full text-sm">
              <thead className="bg-[hsl(var(--muted))] text-[#0e2b26]">
                <tr>
                  <th className="text-left px-4 py-3 font-bold uppercase tracking-wider text-xs">RW / RT</th>
                  <th className="text-left px-4 py-3 font-bold uppercase tracking-wider text-xs">Status</th>
                  <th className="text-left px-4 py-3 font-bold uppercase tracking-wider text-xs">Tanggal Submit</th>
                  <th className="text-right px-4 py-3 font-bold uppercase tracking-wider text-xs">Total Penduduk</th>
                  <th className="text-right px-4 py-3 font-bold uppercase tracking-wider text-xs">Aksi</th>
                </tr>
              </thead>
              <tbody data-testid="status-table-body">
                {loading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <tr key={`sk-row-${i}`} className="border-t border-[hsl(var(--border))]">
                      {Array.from({ length: 5 }).map((_, j) => (
                        <td key={`sk-cell-${i}-${j}`} className="px-4 py-3"><div className="skeleton h-4 w-20"></div></td>
                      ))}
                    </tr>
                  ))
                ) : rows.map((r) => (
                  <tr key={`${r.rw}-${r.rt}`} className="border-t border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]/50 transition">
                    <td className="px-4 py-3 font-semibold text-[#0e2b26]">RW {r.rw} / RT {r.rt}</td>
                    <td className="px-4 py-3">
                      <span className={`badge-pill ${r.status === "SUDAH MENGISI" ? "badge-sudah" : "badge-belum"}`}>
                        {r.status === "SUDAH MENGISI" ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
                        {r.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[hsl(var(--muted-foreground))]">{r.tanggal_submit || "-"}</td>
                    <td className="px-4 py-3 text-right font-semibold text-[#0e2b26]">
                      {r.total_penduduk != null ? `${fmtNum(r.total_penduduk)} Jiwa` : "-"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => nav(`/detail?rw=${r.rw}&rt=${r.rt}&tahun=${filter.tahun}&bulan=${filter.bulan}`)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold text-[#0e2b26] hover:bg-[hsl(var(--muted))]"
                        data-testid={`view-${r.rw}-${r.rt}`}
                      >
                        Detail <ExternalLink size={12} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-[hsl(var(--border))]">
            {loading ? Array.from({ length: 6 }).map((_, i) => (
              <div key={`sk-card-${i}`} className="p-4"><div className="skeleton h-16 w-full"></div></div>
            )) : rows.map((r) => (
              <div key={`${r.rw}-${r.rt}`} className="p-4 flex items-center justify-between gap-3" data-testid={`row-${r.rw}-${r.rt}`}>
                <div className="min-w-0">
                  <div className="font-bold text-[#0e2b26]">RW {r.rw} / RT {r.rt}</div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">{r.tanggal_submit || "-"}</div>
                  <div className="text-xs mt-1">
                    {r.total_penduduk != null ? <span className="font-semibold text-[#0e2b26]">{fmtNum(r.total_penduduk)} Jiwa</span> : <span className="text-[hsl(var(--muted-foreground))]">-</span>}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <span className={`badge-pill ${r.status === "SUDAH MENGISI" ? "badge-sudah" : "badge-belum"}`}>
                    {r.status === "SUDAH MENGISI" ? <CheckCircle2 size={11} /> : <XCircle size={11} />}
                    {r.status === "SUDAH MENGISI" ? "SUDAH" : "BELUM"}
                  </span>
                  <button
                    onClick={() => nav(`/detail?rw=${r.rw}&rt=${r.rt}&tahun=${filter.tahun}&bulan=${filter.bulan}`)}
                    className="text-xs font-semibold text-[#0e6b4f]"
                  >Detail →</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
